/**
 * Entity types the backend actually applies `deactivation_config` to. For every
 * other entity type the field is a no-op, so we never send it.
 */
export const ENTITIES_WITH_DEACTIVATION_CONFIG = new Set<string>([
  'accounting_reference',
  'site',
]);

/** Inclusive bounds for a day-offset, matching the backend `ge=0, le=3650`. */
export const MIN_DAY_OFFSET = 0;
export const MAX_DAY_OFFSET = 3650;

/**
 * Backend day-offset defaults (both 180 days, per the pydantic DeactivationConfig
 * model). Kept as documented FE constants (not imported from python) and used
 * only as input placeholder hints for untouched fields. `days_after_start`
 * defaults to null (off), so it has no default-number hint here.
 */
export const DEFAULT_AFTER_END_DAYS = 180;
export const DEFAULT_BEFORE_START_DAYS = 180;

/**
 * Normalizes a raw number-input string to the API value. Empty input disables
 * the rule (null); otherwise the value is truncated to an integer and clamped to
 * the schema bounds. Meant to run on blur, never mid-keystroke.
 */
export function clampDayOffset(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;

  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;

  return Math.min(Math.max(Math.trunc(parsed), MIN_DAY_OFFSET), MAX_DAY_OFFSET);
}

/**
 * Drops `deactivationConfig` from a task payload when its entity type does not
 * support it. The wizard already clears the field when the entity type changes,
 * but this is the payload-level guarantee: a stale config (e.g. configured for a
 * site, then switched to another entity) is never submitted for an entity the
 * backend would ignore it for.
 */
export function omitUnsupportedDeactivationConfig<
  T extends { entityType: string; deactivationConfig?: unknown },
>(task: T): T {
  if (ENTITIES_WITH_DEACTIVATION_CONFIG.has(task.entityType)) return task;

  const { deactivationConfig: _deactivationConfig, ...rest } = task;
  return rest as T;
}
